// =========================
// SEARCH GRID COMPONENT
// =========================
// External Libraries
import { LitElement, html, css } from 'lit';

// App Modules
import App from '../App';
import Utils from '../Utils';

// This component is used to display a search grid for suppliers or users
// It allows searching, sorting, and filtering of items
class PopinSearchGrid extends LitElement {
  static properties = {
    type: { type: String },
    items: { type: Array },
    query: { type: String },
    page: { type: Number },
    loading: { type: Boolean },
    allLoaded: { type: Boolean },
    sortBy: { type: String },
    filterByService: { type: Array },
    services: { type: Array },
    filterByCategory: { type: Array },
  };

  constructor() {
    super();
    this.type = 'supplier';
    this.items = [];
    this.query = '';
    this.page = 1;
    this.loading = false;
    this.allLoaded = false;
    this.pageSize = 12;
    this.sortBy = 'az';
    this.filterByService = [];
    this.services = [];
    this.filterByCategory = [];
  }

  // This function is called when the component is first connected to the DOM
  connectedCallback() {
    super.connectedCallback();
    this.initObserver();
  }

  // This function is called when the component is disconnected from the DOM
  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.observer) this.observer.disconnect();
  }

  // This function is called when the component is first updated
  async firstUpdated() {
    await this.fetchServices();
    this.loadItems();
  }

  // Fetches the list of services from the API
  async fetchServices() {
    try {
      const res = await fetch(`${App.apiBase}/service`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      if (res.ok) {
        this.services = await res.json();
      }
    } catch (err) {
      console.error('Failed to fetch services:', err);
    }
  }

  // Loads items from the API based on the current page, query, and filters
  async loadItems() {
    if (this.loading || this.allLoaded) return;

    this.loading = true;
    try {
      let url = `${App.apiBase}/${this.type}?page=${this.page}&limit=${this.pageSize}`;

      if (this.query) url += `&q=${encodeURIComponent(this.query)}`;

      if (this.type === 'user') {
        url += `&role=user`;
      }

      if (this.type === 'supplier' && this.filterByService.length > 0) {
        const encoded = this.filterByService
          .map((s) => encodeURIComponent(s))
          .join(',');
        url += `&service=${encoded}`;
      }

      if (this.type === 'event' && this.filterByCategory.length > 0) {
        const encoded = this.filterByCategory
          .map((c) => encodeURIComponent(c))
          .join(',');
        url += `&category=${encoded}`;
      }

      const res = await fetch(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });

      if (!res.ok) throw new Error(`Failed to fetch ${this.type}`);

      let data = await res.json();
      if (data.length < this.pageSize) this.allLoaded = true;

      data.sort((a, b) => {
        const nameA =
          a.businessName?.toLowerCase() || a.firstName?.toLowerCase() || '';
        const nameB =
          b.businessName?.toLowerCase() || b.firstName?.toLowerCase() || '';
        return this.sortBy === 'az'
          ? nameA.localeCompare(nameB)
          : nameB.localeCompare(nameA);
      });

      this.items = [...this.items, ...data];
      this.page++;
    } catch (err) {
      console.error('Error loading items:', err);
    } finally {
      this.loading = false;
      this.requestUpdate();
    }
  }

  // Initializes the Intersection Observer for lazy loading
  initObserver() {
    const options = {
      root: null,
      rootMargin: '100px',
      threshold: 0.1,
    };

    this.observer = new IntersectionObserver(async (entries) => {
      if (entries[0].isIntersecting && !this.loading) {
        await this.loadItems();
      }
    }, options);
  }

  // This function is called when the component is updated
  updated() {
    const sentinel = this.renderRoot?.querySelector('#sentinel');
    if (sentinel && this.observer) this.observer.observe(sentinel);
  }

  // Event handlers for input changes
  handleSearchInput(e) {
    this.query = e.target.value;
    this.resetAndLoad();
  }

  // Event handlers for sort and filter changes
  handleSortChange(e) {
    this.sortBy = e.target.value;
    this.resetAndLoad();
  }

  // This function handles the filter change event
  handleFilterChange(e) {
    this.filterByService = [...e.target.value];
    this.resetAndLoad();
  }

  // This function handles the category filter change event
  handleCategoryFilterChange(e) {
    this.filterByCategory = [...e.target.value];
    this.resetAndLoad();
  }

  // Resets the page and items, and reloads the items
  resetAndLoad() {
    this.page = 1;
    this.items = [];
    this.allLoaded = false;
    this.loadItems();
  }

  // This function handles the filter button click event
  handleFilterClick() {
    const popup = this.shadowRoot.getElementById('filterPopup');
    if (popup) popup.active = !popup.active;
  }

  // This function renders the card based on the type
  renderCard(item) {
    switch (this.type) {
      case 'supplier':
        return html`<popin-supplier-card
          .supplier=${item}
        ></popin-supplier-card>`;
      case 'user':
        return html`<popin-user-card .user=${item}></popin-user-card>`;
      case 'event':
        return html`<popin-event-card .event=${item}></popin-event-card>`;
      default:
        return html`<p>Unsupported type</p>`;
    }
  }

  render() {
    return html`
      <div class="search-grid-container">
        <div class="top-controls">
          <sl-input
            placeholder=${this.type === 'supplier'
              ? 'Search suppliers...'
              : this.type === 'event'
                ? 'Search events...'
                : 'Search friends...'}
            clearable
            @input=${this.handleSearchInput}
          ></sl-input>

          <sl-button id="search-filter" @click=${this.handleFilterClick}>
            <sl-icon name="funnel-fill"></sl-icon>
          </sl-button>

          <sl-select
            placeholder="Sort By"
            @sl-change=${this.handleSortChange}
            value=${this.sortBy}
          >
            <sl-option value="az">Sort Alphabetically A-Z</sl-option>
            <sl-option value="za">Sort Alphabetically Z-A</sl-option>
          </sl-select>

          ${this.type === 'supplier' || this.type === 'event'
            ? html`
                <sl-popup
                  id="filterPopup"
                  class="filter-popup"
                  placement="bottom-end"
                  anchor="search-filter"
                  arrow
                >
                  <div class="filter-content">
                    ${this.type === 'supplier'
                      ? html`
                          <sl-select
                            placeholder="Filter by Service"
                            multiple
                            @sl-change=${this.handleFilterChange}
                            .value=${this.filterByService}
                          >
                            ${this.services.map(
                              (service) => html`
                                <sl-option
                                  value="${encodeURIComponent(service.name)}"
                                >
                                  ${service.name}
                                </sl-option>
                              `
                            )}
                          </sl-select>
                        `
                      : ''}
                    ${this.type === 'event'
                      ? html`
                          <sl-select
                            placeholder="Filter by Category"
                            multiple
                            @sl-change=${this.handleCategoryFilterChange}
                            .value=${this.filterByCategory}
                          >
                            ${[
                              'social',
                              'educational',
                              'networking',
                              'entertainment',
                            ].map(
                              (category) => html`
                                <sl-option value="${category}">
                                  ${category.charAt(0).toUpperCase() +
                                  category.slice(1)}
                                </sl-option>
                              `
                            )}
                          </sl-select>
                        `
                      : ''}
                  </div>
                </sl-popup>
              `
            : ''}
        </div>

        <div class="grid">
          ${this.items.map((item) => this.renderCard(item))}
        </div>

        ${this.loading ? html`<p class="loading">Loading...</p>` : ''}
        <div id="sentinel"></div>
      </div>
    `;
  }

  static styles = css`
    .search-grid-container sl-input {
      width: 70%;
    }

    .top-controls {
      position: sticky;
      top: 0;
      background: white;
      display: flex;
      flex-wrap: wrap;
      gap: 1rem;
      align-items: center;
      padding: 1rem 0;
      margin-bottom: 1.5rem;
      border-bottom: 1px solid var(--sl-color-gray-200);
    }

    .filter-content,
    .filter-popup {
      padding: 1rem;
      border-radius: 12px;
      max-width: 300px;
      background-color: var(--white);
    }

    .filter-popup::part(popup) {
      margin: 20px 0px;
      border-radius: 12px;
      box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
      background-color: var(--white);
      border: none;
    }

    .filter-popup::part(arrow) {
      background-color: var(--white);
    }

    sl-select {
      min-width: 250px;
    }

    sl-input::part(base),
    sl-select::part(base) {
      background-color: var(--sl-color-primary-100);
      border: none;
      font-size: 1rem;
      border-radius: 12px;
    }

    .grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
      gap: 2rem;
    }

    .loading {
      text-align: center;
      font-size: 1rem;
      color: var(--sl-color-gray-500);
      margin-top: 2rem;
    }

    @media all and (max-width: 768px) {
      .search-grid-container {
        padding: 1rem;
      }

      .grid {
        grid-template-columns: repeat(auto-fill, minmax(100%, 1fr));
      }
    }
  `;
}

customElements.define('popin-search-grid', PopinSearchGrid);
